import { useState, useEffect, useCallback } from "react";
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Calendar, Sparkles, Loader2, Copy, ArrowUpCircle, ArrowDownCircle, Wallet, PieChart, FileText, Bot, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import DOMPurify from "dompurify";

const AdvancedAccountingPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  
  // Financial Dashboard Data
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [monthlyPnl, setMonthlyPnl] = useState([]);
  
  // Daily Report
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [dailyReport, setDailyReport] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  
  // AI Daily Report
  const [aiReport, setAiReport] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  
  // Financial Report with filters
  const [reportPeriod, setReportPeriod] = useState("month");
  const [finReport, setFinReport] = useState(null);
  const [finLoading, setFinLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, balRes, pnlRes] = await Promise.all([
        api.get("/financial-summary", token),
        api.get("/cash-balance", token),
        api.get("/accounting/monthly-pnl?months=6", token)
      ]);
      setSummary(sumRes.data);
      setBalance(balRes.data);
      setMonthlyPnl(pnlRes.data);
    } catch (err) { console.error(err); toast.error("خطأ في تحميل البيانات"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchDailyReport = async () => {
    setDailyLoading(true);
    try {
      const res = await api.get(`/accounting/daily-report?date=${dailyDate}`, token);
      setDailyReport(res.data);
    } catch (err) { console.error(err); toast.error("خطأ"); }
    finally { setDailyLoading(false); }
  };

  const fetchFinReport = async () => {
    setFinLoading(true);
    try {
      const res = await api.get(`/financial-report?period=${reportPeriod}`, token);
      setFinReport(res.data);
    } catch (err) { console.error(err); toast.error("خطأ"); }
    finally { setFinLoading(false); }
  };

  const getAiDailyReport = async () => {
    setAiLoading(true);
    setAiReport("");
    try {
      const res = await api.post(`/accounting/ai-daily-report?date=${dailyDate}`, {}, token);
      setAiReport(res.data.report);
    } catch (err) { console.error(err); toast.error("خطأ في التقرير الذكي"); }
    finally { setAiLoading(false); }
  };

  const fmt = (n) => (n || 0).toLocaleString();
  
  const formatAI = (text) => {
    if (!text) return null;
    const html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>').replace(/\n/g, '<br/>');
    return <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
  };

  const changeDay = (dir) => {
    const d = new Date(dailyDate);
    d.setDate(d.getDate() + dir);
    setDailyDate(d.toISOString().split("T")[0]);
  };

  const maxPnl = Math.max(...monthlyPnl.map(m => Math.max(m.income, m.expenses)), 1);

  const tabs = [
    { id: "dashboard", label: "لوحة مالية", icon: BarChart3 },
    { id: "daily", label: "تقرير اليوم", icon: Calendar },
    { id: "pnl", label: "أرباح وخسائر", icon: TrendingUp },
    { id: "categories", label: "تصنيف المصروفات", icon: PieChart },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">جاري التحميل...</div></div>;

  return (
    <div className="animate-fade-in" data-testid="advanced-accounting">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          المحاسبة المتقدمة
        </h1>
        <p className="text-gray-500 text-sm mt-1">لوحة مالية شاملة مع تحليل ذكي</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === "daily") fetchDailyReport(); if (tab.id === "categories") fetchFinReport(); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === tab.id ? "bg-emerald-600 text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* ===== DASHBOARD TAB ===== */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="kpi-total-balance">
              <div className="flex items-center gap-2 mb-2"><Wallet className="w-5 h-5 text-emerald-500" /><span className="text-xs text-gray-500 font-medium">الرصيد الكلي</span></div>
              <p className="text-2xl font-bold text-emerald-600">{fmt(balance?.total_balance)}</p>
              <p className="text-[10px] text-gray-400">{fmt(balance?.transactions_count)} معاملة</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="kpi-today">
              <div className="flex items-center gap-2 mb-2"><Calendar className="w-5 h-5 text-blue-500" /><span className="text-xs text-gray-500 font-medium">صافي اليوم</span></div>
              <p className={`text-2xl font-bold ${(summary?.today?.net || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(summary?.today?.net)}</p>
              <p className="text-[10px] text-gray-400">إيراد {fmt(summary?.today?.income)} | مصروف {fmt(summary?.today?.expenses)}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="kpi-month">
              <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5 text-purple-500" /><span className="text-xs text-gray-500 font-medium">صافي الشهر</span></div>
              <p className={`text-2xl font-bold ${(summary?.month?.net || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(summary?.month?.net)}</p>
              <p className="text-[10px] text-gray-400">إيراد {fmt(summary?.month?.income)} | مصروف {fmt(summary?.month?.expenses)}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="kpi-total-income">
              <div className="flex items-center gap-2 mb-2"><DollarSign className="w-5 h-5 text-amber-500" /><span className="text-xs text-gray-500 font-medium">إجمالي الإيرادات</span></div>
              <p className="text-2xl font-bold text-gray-900">{fmt(balance?.total_income)}</p>
              <p className="text-[10px] text-gray-400">مصروفات: {fmt(balance?.total_expenses)}</p>
            </div>
          </div>

          {/* Monthly Chart */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-500" /> الأداء الشهري</h3>
            <div className="space-y-3">
              {monthlyPnl.map(m => (
                <div key={m.month} className="space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-medium text-gray-700">{m.month}</span>
                    <div className="flex gap-4">
                      <span className="text-emerald-600 font-bold">{fmt(m.income)}</span>
                      <span className="text-red-500 font-bold">{fmt(m.expenses)}</span>
                      <span className={`font-bold ${m.net_profit >= 0 ? "text-blue-600" : "text-red-600"}`}>صافي: {fmt(m.net_profit)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 h-5">
                    <div className="bg-emerald-400 rounded-sm h-full transition-all" style={{ width: `${(m.income / maxPnl) * 100}%` }} />
                    <div className="bg-red-400 rounded-sm h-full transition-all" style={{ width: `${(m.expenses / maxPnl) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-6 mt-3 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-400" /> إيرادات</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-400" /> مصروفات</span>
            </div>
          </div>
        </div>
      )}

      {/* ===== DAILY REPORT TAB ===== */}
      {activeTab === "daily" && (
        <div className="space-y-4">
          {/* Date Selector */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center justify-between">
            <button onClick={() => { changeDay(-1); }} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
            <div className="flex items-center gap-3">
              <input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} className="px-3 py-2 border rounded-xl text-sm" data-testid="daily-date-input" />
              <button onClick={fetchDailyReport} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700" data-testid="load-daily-btn">عرض التقرير</button>
              <button onClick={getAiDailyReport} disabled={aiLoading} className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-50" data-testid="ai-daily-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />} تقرير ذكي
              </button>
            </div>
            <button onClick={() => { changeDay(1); }} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
          </div>

          {/* AI Report */}
          {aiReport && (
            <div className="bg-violet-50 border border-violet-200 rounded-2xl p-5">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-bold text-violet-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> تقرير نهاية اليوم الذكي - {dailyDate}</span>
                <button onClick={() => navigator.clipboard.writeText(aiReport).then(() => toast.success("تم النسخ"))} className="text-violet-400 hover:text-violet-600"><Copy className="w-4 h-4" /></button>
              </div>
              {formatAI(aiReport)}
            </div>
          )}

          {/* Daily Data */}
          {dailyLoading ? <div className="text-center py-8 text-gray-400">جاري التحميل...</div> : dailyReport && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Summary cards */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                <ArrowUpCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-xs text-gray-500">إيرادات اليوم</p>
                <p className="text-2xl font-bold text-emerald-600">{fmt(dailyReport.total_income)}</p>
                <p className="text-[10px] text-gray-400">{dailyReport.income_items_count} معاملة</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
                <ArrowDownCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-xs text-gray-500">مصروفات اليوم</p>
                <p className="text-2xl font-bold text-red-600">{fmt(dailyReport.total_expenses)}</p>
                <p className="text-[10px] text-gray-400">{dailyReport.expense_items_count} معاملة</p>
              </div>
              <div className={`${dailyReport.net_profit >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'} border rounded-2xl p-5 text-center`}>
                <Wallet className={`w-8 h-8 ${dailyReport.net_profit >= 0 ? 'text-blue-500' : 'text-amber-500'} mx-auto mb-2`} />
                <p className="text-xs text-gray-500">صافي اليوم</p>
                <p className={`text-2xl font-bold ${dailyReport.net_profit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>{fmt(dailyReport.net_profit)}</p>
              </div>

              {/* Income details */}
              {Object.keys(dailyReport.income_by_category || {}).length > 0 && (
                <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-5">
                  <h4 className="font-bold text-gray-800 mb-3 text-sm flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-emerald-500" /> تفاصيل الإيرادات</h4>
                  <div className="space-y-2">
                    {Object.entries(dailyReport.income_by_category).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">{cat}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400">{data.count} معاملة</span>
                          <span className="font-bold text-emerald-600 text-sm">{fmt(data.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expense details */}
              {Object.keys(dailyReport.expense_by_category || {}).length > 0 && (
                <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-5">
                  <h4 className="font-bold text-gray-800 mb-3 text-sm flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-red-500" /> تفاصيل المصروفات</h4>
                  <div className="space-y-2">
                    {Object.entries(dailyReport.expense_by_category).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">{cat}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400">{data.count} معاملة</span>
                          <span className="font-bold text-red-600 text-sm">{fmt(data.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Patients who paid */}
              {dailyReport.patients_paid?.length > 0 && (
                <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-5">
                  <h4 className="font-bold text-gray-800 mb-3 text-sm">المرضى اللي دفعوا اليوم</h4>
                  <div className="space-y-1">
                    {dailyReport.patients_paid.map((p, i) => (
                      <div key={`paid-${i}`} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                        <span className="font-medium">{p.name}</span>
                        <span className="text-xs text-gray-500">{p.category}</span>
                        <span className="font-bold text-emerald-600">{fmt(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== P&L TAB ===== */}
      {activeTab === "pnl" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" /> تقرير الأرباح والخسائر الشهري</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">الشهر</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-emerald-600">الإيرادات</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-red-600">المصروفات</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-blue-600">صافي الربح</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">هامش الربح</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">العمليات</th>
                    <th className="text-right py-3 px-4 text-xs font-bold text-gray-500">المعاملات</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyPnl.map(m => (
                    <tr key={m.month} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4 font-bold text-sm">{m.month}</td>
                      <td className="py-3 px-4 text-sm text-emerald-600 font-medium">{fmt(m.income)}</td>
                      <td className="py-3 px-4 text-sm text-red-600 font-medium">{fmt(m.expenses)}</td>
                      <td className={`py-3 px-4 text-sm font-bold ${m.net_profit >= 0 ? "text-blue-600" : "text-red-600"}`}>{fmt(m.net_profit)}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${m.margin >= 30 ? "bg-emerald-100 text-emerald-700" : m.margin >= 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{m.margin}%</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{m.patients_count}</td>
                      <td className="py-3 px-4 text-sm text-gray-400">{m.transactions_count}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  {monthlyPnl.length > 0 && (
                    <tr className="bg-gray-100 font-bold">
                      <td className="py-3 px-4 text-sm">المجموع</td>
                      <td className="py-3 px-4 text-sm text-emerald-700">{fmt(monthlyPnl.reduce((s, m) => s + m.income, 0))}</td>
                      <td className="py-3 px-4 text-sm text-red-700">{fmt(monthlyPnl.reduce((s, m) => s + m.expenses, 0))}</td>
                      <td className="py-3 px-4 text-sm text-blue-700">{fmt(monthlyPnl.reduce((s, m) => s + m.net_profit, 0))}</td>
                      <td className="py-3 px-4" />
                      <td className="py-3 px-4 text-sm text-gray-600">{monthlyPnl.reduce((s, m) => s + m.patients_count, 0)}</td>
                      <td className="py-3 px-4 text-sm text-gray-400">{monthlyPnl.reduce((s, m) => s + m.transactions_count, 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== CATEGORIES TAB ===== */}
      {activeTab === "categories" && (
        <div className="space-y-4">
          <div className="flex gap-2 mb-4">
            {["day", "week", "month", "year"].map(p => (
              <button key={p} onClick={() => { setReportPeriod(p); setTimeout(fetchFinReport, 100); }}
                className={`px-4 py-2 rounded-xl text-sm font-bold ${reportPeriod === p ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                {p === "day" ? "اليوم" : p === "week" ? "الأسبوع" : p === "month" ? "الشهر" : "السنة"}
              </button>
            ))}
          </div>

          {finLoading ? <div className="text-center py-8 text-gray-400">جاري التحميل...</div> : finReport && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Income Categories */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <ArrowUpCircle className="w-5 h-5 text-emerald-500" /> تصنيف الإيرادات
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold mr-auto">{fmt(finReport.total_income)}</span>
                </h4>
                {Object.keys(finReport.income_by_category || {}).length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">لا توجد إيرادات</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(finReport.income_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => (
                      <div key={cat}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700">{cat}</span>
                          <span className="font-bold text-emerald-600">{fmt(amount)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-emerald-400 h-2 rounded-full" style={{ width: `${(amount / finReport.total_income) * 100}%` }} />
                        </div>
                        <p className="text-[10px] text-gray-400 text-left">{((amount / finReport.total_income) * 100).toFixed(1)}%</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Expense Categories */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <ArrowDownCircle className="w-5 h-5 text-red-500" /> تصنيف المصروفات
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold mr-auto">{fmt(finReport.total_expenses)}</span>
                </h4>
                {Object.keys(finReport.expenses_by_category || {}).length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">لا توجد مصروفات</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(finReport.expenses_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => (
                      <div key={cat}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700">{cat}</span>
                          <span className="font-bold text-red-600">{fmt(amount)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-red-400 h-2 rounded-full" style={{ width: `${(amount / finReport.total_expenses) * 100}%` }} />
                        </div>
                        <p className="text-[10px] text-gray-400 text-left">{((amount / finReport.total_expenses) * 100).toFixed(1)}%</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Net Profit Summary */}
              <div className="lg:col-span-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">إجمالي الإيرادات</p>
                    <p className="text-xl font-bold text-emerald-600">{fmt(finReport.total_income)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">إجمالي المصروفات</p>
                    <p className="text-xl font-bold text-red-600">{fmt(finReport.total_expenses)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">صافي الربح</p>
                    <p className={`text-xl font-bold ${finReport.net_profit >= 0 ? "text-blue-600" : "text-red-600"}`}>{fmt(finReport.net_profit)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedAccountingPage;
